import { Injectable } from 'angular-ts-decorators';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { Domain } from '@shared/app/scripts/services/domain';
import { Initable } from '@shared/app/scripts/services/initable';
import type { Language } from '@shared/app/scripts/services/language';
import type { LocalStorage } from '@shared/app/scripts/services/local-storage';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SessionRequests } from '@shared/app/scripts/services/session-requests';

/**
 * Service that exposes basic information about current session
 *
 * Implements {@link sharedModule.Initable Initable} interface.
 */
@Injectable('session')
export class Session implements Initable {
  private currentSubject: Subject<CurrentSession>;
  private _current$: Observable<CurrentSession>;

  private SESSION_COOKIE = 'sid';

  constructor(
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private $location: ng.ILocationService,
    private currentSession: CurrentSession,
    private domain: Domain,
    private ipCookie,
    private language: Language,
    private localStorage: LocalStorage,
    private sessionRequests: SessionRequests,
    private sentry: Sentry,
    private $document: ng.IDocumentService,
    // TODO consider making it private
    // TODO rename it to redirectAnonymousToLogin
    public redirectAnonymousToGate: boolean,
  ) {
    'ngInject';

    // TODO add an alternative stream without default values at first
    this.currentSubject = new BehaviorSubject(
      angular.copy(this.currentSession),
    );

    /**
     *
     * Stream that emits it's value every time when the session is updated
     */
    this._current$ = this.currentSubject.asObservable();
  }

  // TODO flatten this thing, why would we want to support behavior like this?
  set current$(value: Observable<any>) {
    this._current$ = value;
  }

  get current$(): Observable<any> {
    return this._current$;
  }

  /**
   * Helper method to finish session initialization
   * Sets sentry user, language, localStorage and resolves the session
   */
  private finishSessionInit(
    current: any,
    sid: string,
    sessionDeferred: ng.IDeferred<CurrentSession>,
  ): void {
    this.sentry.setUser({
      email: current.email,
      username: current.username,
      segment: current.type,
    });

    if (this.currentSession.language) {
      this.language.set(this.currentSession.language);
    }

    this.localStorage.setItem('session', JSON.stringify(current));

    sessionDeferred.resolve(this.currentSession);

    this.currentSubject.next(angular.copy(this.currentSession));
  }

  /**
   * A naive helper that attempts to derive a root domain from a hostname.
   * This works well for typical "sub.domain.com" structures but is not
   * fully robust for *every* possible TLD.
   */
  private getCookieRootDomain(hostname: string): string | undefined {
    if (!hostname) {
      return undefined;
    }
    const parts = hostname.split('.');
    // We only support 2 segments, e.g. 'mydomain.com'
    if (parts.length > 2) {
      return '.' + parts.slice(parts.length - 2).join('.');
    } else {
      return '.' + hostname;
    }
  }

  /**
   *
   * Check if the session id is available on client & if session is
   * recognized on server. It should be called automatically on the start
   * up of the application. Depending of `redirectAnonymousToGate`
   * configuration it can redirect to gate in case of missing the session.
   *
   * POS-2971: use sid param from route if different from current cookie
   *
   * @returns {promise} it will resolve with session information or reject
   * with error description.
   */
  init(): ng.IPromise<CurrentSession> {
    const sidParam = this.$location.search().sid;
    const currentCookieSid = this.ipCookie(this.SESSION_COOKIE);
    const cookieDomain = this.getCookieRootDomain(
      this.$window.location.hostname,
    );

    if (sidParam && sidParam !== currentCookieSid) {
      this.ipCookie(this.SESSION_COOKIE, sidParam, {
        path: '/',
        domain: cookieDomain,
        expires: 1,
      });
      if (!cookieDomain.includes('.lc')) {
        this.$document[0].cookie = `${this.SESSION_COOKIE}=${sidParam}; domain=${cookieDomain}; SameSite=None; Secure; path=/`;
      }
      // Remove sid from URL after consuming it to prevent loops with expired sids
      this.$location.search('sid', null);
    }

    // Use sidParam if it exists, otherwise try to read from cookie
    const sid = sidParam || this.ipCookie(this.SESSION_COOKIE);
    const sessionDeferred: ng.IDeferred<CurrentSession> = this.$q.defer();
    let sessionPromise = sessionDeferred.promise,
      storedSession;

    if (sid) {
      // we have the cookie, maybe user's session is recognized by the server
      this.sessionRequests.getCurrent().subscribe(
        (current) => {
          // For SSO: get a fresh session by calling set-tenant with current tenant
          // This keeps the old session active while giving us a new one
          if (sidParam && current.current_tenant) {
            this.sessionRequests.setTenant(current.current_tenant).then(
              (setTenantResponse) => {
                // Update to use the new sid from set-tenant response
                const newSid = setTenantResponse.sid;
                this.ipCookie(this.SESSION_COOKIE, newSid, {
                  path: '/',
                  domain: cookieDomain,
                  expires: 1,
                });
                if (!cookieDomain.includes('.lc')) {
                  this.$document[0].cookie = `${this.SESSION_COOKIE}=${newSid}; domain=${cookieDomain}; SameSite=None; Secure; path=/`;
                }

                // Update current session with new sid
                this.currentSession.setTo(current, newSid);
                this.finishSessionInit(current, newSid, sessionDeferred);
              },
              (error) => {
                // If set-tenant fails, fall back to using the original session
                this.currentSession.setTo(current, sid);
                this.finishSessionInit(current, sid, sessionDeferred);
              },
            );
          } else {
            // No sidParam or no current_tenant, use session as-is
            this.currentSession.setTo(current, sid);
            this.finishSessionInit(current, sid, sessionDeferred);
          }
        },
        (error) => {
          try {
            storedSession = JSON.parse(this.localStorage.getItem('session'));
          } catch (e) {
            // ignore error while parsing
          }

          // try reading data from localStorage
          if (
            storedSession &&
            storedSession.sid === sid &&
            error.status !== 403
          ) {
            angular.copy(storedSession, this.currentSession);

            sessionDeferred.resolve(this.currentSession);
            this.currentSubject.next(angular.copy(this.currentSession));
          } else if (this.redirectAnonymousToGate && error.status !== -1) {
            this.$window.location.href = this.domain.link(
              'login#/login?redirect=' +
                encodeURIComponent(this.$window.location.href),
            );
          } else {
            sessionDeferred.reject(
              error && error.data ? error.data.message : '',
            );
          }
        },
      );
    } else {
      // the session is anonymous
      if (this.redirectAnonymousToGate) {
        this.$window.location.href = this.domain.link(
          'login#/login?redirect=' +
            encodeURIComponent(this.$window.location.href),
        );
      } else {
        sessionDeferred.reject('Session cookie is not available');

        // Emit empty session, so it works for reloading
        this.currentSubject.next(angular.copy(this.currentSession));
      }
    }

    return sessionPromise;
  }
}
